import { useState, type ReactNode } from "react";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { RetryProcessingAction, type RevisionRetry } from "@/components/projects/revision-retry";
import {
  ChevronDown,
  ChevronRight,
  Download,
  FolderOpen,
  Loader2,
  PencilLine,
  Table2,
  Upload,
} from "lucide-react";

import { RowAction, RowActionSeparator, RowActionsMenu } from "@/components/row-actions-menu";
import { RevisionBadge } from "@/components/semantic-badges";
import { Checkbox } from "@/components/ui/checkbox";
import { IconButton } from "@/components/ui/icon-button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentTypeLabel, revisionTypeLabelProps } from "@/components/document-type-label";
import { cn, formatDate } from "@/lib/utils";
import { buildDocumentDownloadFilename } from "@/lib/document-download";
import { useCanEdit } from "@/lib/capabilities";

type ListedRevision = Doc<"projectDocumentRevisions"> & { indexingError?: string };

export type ProjectDocumentListItem = Doc<"projectDocuments"> & {
  folderPath: string;
  latestRevision: ListedRevision | null;
  revisions: ListedRevision[];
};

interface ProjectDocumentsTableProps {
  documents: ProjectDocumentListItem[];
  selectedDocumentIds?: Set<string>;
  areAllDocumentsSelected?: boolean;
  hasSomeDocumentsSelected?: boolean;
  onToggleAllDocuments?: (checked: boolean) => void;
  onToggleDocumentSelection?: (documentId: Id<"projectDocuments">, checked: boolean) => void;
  onOpenDocument: (documentId: Id<"projectDocuments">) => void;
  onRenameDocument?: (document: ProjectDocumentListItem) => void;
  onUploadRevision?: (documentId: Id<"projectDocuments">) => void;
  onDownloadDocument?: (document: ProjectDocumentListItem) => void;
  onMoveDocument?: (document: ProjectDocumentListItem) => void;
  onEditScheduleTables?: (document: ProjectDocumentListItem) => void;
  /** Retry for a revision whose processing failed; null when it can't be retried. */
  getRevisionRetry?: (revision: ListedRevision) => RevisionRetry | null;
  isUploadingRevision?: boolean;
  revisionUploadDocumentId?: Id<"projectDocuments"> | null;
  downloadingDocumentId?: Id<"projectDocuments"> | null;
  showFolderColumn?: boolean;
  emptyMessage?: string;
  compact?: boolean;
}

function getDocumentFolderLabel(folderPath: string): string {
  return folderPath || "Project Documents";
}

export interface DocumentActionsMenuProps {
  document: Pick<ProjectDocumentListItem, "_id" | "name">;
  latestRevision: Pick<
    Doc<"projectDocumentRevisions">,
    "latestSharedScheduleExtractionId"
  > | null;
  onRenameDocument?: () => void;
  onUploadRevision?: () => void;
  onDownloadDocument?: () => void;
  onMoveDocument?: () => void;
  onEditScheduleTables?: () => void;
  /** Offered when the latest revision's processing failed. */
  retry?: RevisionRetry | null;
  isUploadingRevision?: boolean;
  /** This row's download is in flight (spinner). */
  isDownloading?: boolean;
  /** Any download is in flight (disables Download). */
  isDownloadBusy?: boolean;
  /** Extra revision items rendered after "Schedule tables" (e.g. delete latest revision). */
  revisionActions?: ReactNode;
  /** Destructive items rendered last, after a separator (e.g. delete document). */
  destructiveActions?: ReactNode;
  /** Lets callers load data (delete eligibility) only while the menu is open. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Row actions for a project document, shared by the overview table and the documents library.
 */
export function DocumentActionsMenu({
  document,
  latestRevision,
  onRenameDocument,
  onUploadRevision,
  onDownloadDocument,
  onMoveDocument,
  onEditScheduleTables,
  retry,
  isUploadingRevision = false,
  isDownloading = false,
  isDownloadBusy = false,
  revisionActions,
  destructiveActions,
  onOpenChange,
}: DocumentActionsMenuProps) {
  const canEdit = useCanEdit();
  return (
    <RowActionsMenu label={`Actions for ${document.name}`} onOpenChange={onOpenChange}>
        {onRenameDocument ? (
          <RowAction
            icon={PencilLine}
            onSelect={(event) => {
              event.preventDefault();
              onRenameDocument();
            }}
          >
            Rename
          </RowAction>
        ) : null}
        {onUploadRevision ? (
          <RowAction
            icon={isUploadingRevision ? SpinnerIcon : Upload}
            disabled={isUploadingRevision}
            onSelect={(event) => {
              event.preventDefault();
              onUploadRevision();
            }}
          >
            Upload revision
          </RowAction>
        ) : null}
        {onDownloadDocument ? (
          <RowAction
            icon={isDownloading ? SpinnerIcon : Download}
            disabled={!latestRevision || isDownloadBusy}
            onSelect={(event) => {
              event.preventDefault();
              onDownloadDocument();
            }}
          >
            Download
          </RowAction>
        ) : null}
        {onEditScheduleTables && latestRevision?.latestSharedScheduleExtractionId ? (
          <RowAction
            icon={Table2}
            onSelect={(event) => {
              event.preventDefault();
              onEditScheduleTables();
            }}
          >
            {canEdit ? "Edit schedule tables" : "View schedule tables"}
          </RowAction>
        ) : null}
        {retry ? <RetryProcessingAction {...retry} /> : null}
        {revisionActions}
        {onMoveDocument ? (
          <>
            <RowActionSeparator />
            <RowAction
              icon={FolderOpen}
              onSelect={(event) => {
                event.preventDefault();
                onMoveDocument();
              }}
            >
              Move
            </RowAction>
          </>
        ) : null}
        {destructiveActions ? (
          <>
            <RowActionSeparator />
            {destructiveActions}
          </>
        ) : null}
    </RowActionsMenu>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return <Loader2 className={cn(className, "animate-spin")} />;
}

export function ProjectDocumentsTable({
  documents,
  selectedDocumentIds,
  areAllDocumentsSelected = false,
  hasSomeDocumentsSelected = false,
  onToggleAllDocuments,
  onToggleDocumentSelection,
  onOpenDocument,
  onRenameDocument,
  onUploadRevision,
  onDownloadDocument,
  onMoveDocument,
  onEditScheduleTables,
  getRevisionRetry,
  isUploadingRevision = false,
  revisionUploadDocumentId = null,
  downloadingDocumentId = null,
  showFolderColumn = false,
  emptyMessage = "No documents in this location yet.",
  compact = false,
}: ProjectDocumentsTableProps) {
  const [openRevisionDocumentIds, setOpenRevisionDocumentIds] = useState<Set<string>>(() => new Set());
  const showSelectionColumn = Boolean(selectedDocumentIds && onToggleDocumentSelection);
  const showActionsColumn = Boolean(
    onRenameDocument || onUploadRevision || onDownloadDocument || onMoveDocument || onEditScheduleTables
  );
  const emptyColumnCount =
    4 + (showSelectionColumn ? 1 : 0) + (showFolderColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  const handleToggleRevisionHistory = (documentId: Id<"projectDocuments">) => {
    setOpenRevisionDocumentIds((current) => {
      const next = new Set(current);
      const key = String(documentId);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Table className={compact ? "table-fixed text-xs" : undefined}>
      <TableHeader>
        <TableRow>
          {showSelectionColumn ? (
            <TableHead className={compact ? "h-9 w-[60px] px-2" : "w-[92px]"}>
              <Checkbox
                aria-label="Select all visible documents"
                checked={areAllDocumentsSelected ? true : hasSomeDocumentsSelected ? "indeterminate" : false}
                onCheckedChange={(checked) => {
                  onToggleAllDocuments?.(checked === true);
                }}
              />
            </TableHead>
          ) : null}
          <TableHead className={compact ? "h-9 w-[31%] px-2" : undefined}>Name</TableHead>
          {showFolderColumn ? <TableHead className={compact ? "h-9 w-[18%] px-2" : undefined}>Folder</TableHead> : null}
          <TableHead className={compact ? "h-9 w-[24%] px-2" : undefined}>Latest Revision</TableHead>
          <TableHead className={compact ? "h-9 w-[12%] px-2" : undefined}>Type</TableHead>
          <TableHead className={compact ? "h-9 w-[15%] px-2" : "w-[140px]"}>Updated</TableHead>
          {showActionsColumn ? (
            <TableHead className={compact ? "h-9 w-[44px] px-2" : "w-[60px]"}>
              <span className="sr-only">Actions</span>
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.length === 0 ? (
          <TableRow>
            <TableCell colSpan={emptyColumnCount} className="py-10 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          documents.flatMap((document) => {
            const sortedRevisions = (document.revisions ?? [])
              .slice()
              .sort(
                (a, b) =>
                  b.revisionNumber - a.revisionNumber || String(b._id).localeCompare(String(a._id))
              );
            const latestRevision = sortedRevisions[0] ?? document.latestRevision ?? null;
            const historyRevisions = sortedRevisions.slice(1);
            const isRevisionHistoryOpen = openRevisionDocumentIds.has(String(document._id));
            const documentRow = (
              <TableRow key={document._id}>
                {showSelectionColumn ? (
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Checkbox
                        aria-label={`Select ${document.name}`}
                        checked={selectedDocumentIds?.has(String(document._id)) ?? false}
                        onCheckedChange={(checked) => {
                          onToggleDocumentSelection?.(document._id, checked === true);
                        }}
                      />
                      {historyRevisions.length > 0 ? (
                        <IconButton
                          label={`${isRevisionHistoryOpen ? "Hide" : "Show"} revisions for ${document.name}`}
                          icon={isRevisionHistoryOpen ? ChevronDown : ChevronRight}
                          className="text-muted-foreground hover:text-foreground"
                          aria-expanded={isRevisionHistoryOpen}
                          onClick={() => handleToggleRevisionHistory(document._id)}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                ) : null}
                <TableCell className={compact ? "px-2 py-2" : "min-w-[280px]"}>
                  <button
                    type="button"
                    onClick={() => onOpenDocument(document._id)}
                    className="w-full min-w-0 text-left"
                    aria-label={`Open ${document.name}`}
                  >
                    <div className={compact ? "min-w-0" : "min-w-0 space-y-1"}>
                      <p className="truncate font-medium">{document.name}</p>
                      {!compact ? (
                        <p className="text-xs text-muted-foreground">
                          {document.revisionCount} revision{document.revisionCount === 1 ? "" : "s"}
                        </p>
                      ) : null}
                    </div>
                  </button>
                </TableCell>
                {showFolderColumn ? (
                  <TableCell className={cn("truncate text-muted-foreground", compact ? "px-2 py-2 text-xs" : "min-w-[180px] text-sm")}>
                    {getDocumentFolderLabel(document.folderPath)}
                  </TableCell>
                ) : null}
                <TableCell className={compact ? "px-2 py-2" : undefined}>
                  {latestRevision ? (
                    <div className={compact ? "flex min-w-0 items-center gap-1.5" : "flex min-w-0 flex-col items-start gap-1"}>
                      <span className="flex max-w-full items-center gap-2">
                        <RevisionBadge number={latestRevision.revisionNumber} />
                        <span className="truncate text-sm font-medium">{latestRevision.revisionName}</span>
                      </span>
                      {!compact ? (
                        <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                          {buildDocumentDownloadFilename(
                            document.name,
                            latestRevision.originalFilename,
                            latestRevision.revisionNumber
                          )}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">No revisions</span>
                  )}
                </TableCell>
                <TableCell className={compact ? "truncate px-2 py-2 text-xs" : "text-sm"}>
                  <DocumentTypeLabel
                    {...revisionTypeLabelProps(latestRevision)}
                    compact={compact}
                  />
                </TableCell>
                <TableCell className={compact ? "px-2 py-2 text-xs text-muted-foreground" : "text-sm text-muted-foreground"}>
                  {formatDate(document.updatedAt)}
                </TableCell>
                {showActionsColumn ? (
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <DocumentActionsMenu
                      document={document}
                      latestRevision={latestRevision}
                      onRenameDocument={onRenameDocument ? () => onRenameDocument(document) : undefined}
                      onUploadRevision={onUploadRevision ? () => onUploadRevision(document._id) : undefined}
                      onDownloadDocument={
                        onDownloadDocument ? () => onDownloadDocument(document) : undefined
                      }
                      onMoveDocument={onMoveDocument ? () => onMoveDocument(document) : undefined}
                      onEditScheduleTables={
                        onEditScheduleTables ? () => onEditScheduleTables(document) : undefined
                      }
                      isUploadingRevision={
                        isUploadingRevision && revisionUploadDocumentId === document._id
                      }
                      isDownloading={downloadingDocumentId === document._id}
                      isDownloadBusy={Boolean(downloadingDocumentId)}
                      retry={latestRevision ? getRevisionRetry?.(latestRevision) : null}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            );

            if (!isRevisionHistoryOpen) {
              return [documentRow];
            }

            return [
              documentRow,
              ...historyRevisions.map((revision) => (
                <TableRow key={revision._id} className="bg-muted/15">
                  {showSelectionColumn ? <TableCell /> : null}
                  <TableCell className="min-w-[280px] ps-10 text-sm text-muted-foreground">
                    Previous revision
                  </TableCell>
                  {showFolderColumn ? <TableCell /> : null}
                  <TableCell>
                    <div className="flex min-w-0 flex-col items-start gap-1">
                      <span className="flex max-w-full items-center gap-2">
                        <RevisionBadge number={revision.revisionNumber} />
                        <span className="truncate text-sm font-medium">{revision.revisionName}</span>
                      </span>
                      <span className="max-w-full truncate text-xs font-normal text-muted-foreground">
                        {buildDocumentDownloadFilename(
                          document.name,
                          revision.originalFilename,
                          revision.revisionNumber
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <DocumentTypeLabel
                      {...revisionTypeLabelProps(revision)}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(revision._creationTime)}
                  </TableCell>
                  {showActionsColumn ? <TableCell /> : null}
                </TableRow>
              )),
            ];
          })
        )}
      </TableBody>
    </Table>
  );
}
